import { useCallback } from "react";
import { commonPostApi } from "../../utils/apiService.js";
import { API_ENDPOINTS } from "../../utils/apiEndpoints";
import { showToast } from "../../components/common/toast.js";
import { TOAST_TYPE, COMMON_MSG, FEATURE_MSG } from "../../constants/toast";

/**
 * Custom hook for managing role actions (create, edit, add permissions)
 * 
 * @param {Object} params - Parameters object
 * @param {Object} params.rolePermissions - Role permissions hook instance
 * @param {Function} params.fetchRolesByCamp - Function to fetch roles by campaign
 * @param {Function} params.fetchPermissionsTable - Function to fetch permissions table
 * @param {String} params.campaignValue - Current campaign value
 * @param {Function} params.setAddNewRole - Function to set add new role state
 * @param {Function} params.setEditRole - Function to set edit role state
 * @param {Function} params.setEditRolePermission - Function to set edit role permission state
 * @returns {Object} Role action handlers
 */
const useRoleActions = ({
  rolePermissions,
  fetchRolesByCamp,
  fetchPermissionsTable,
  campaignValue,
  setAddNewRole,
  setEditRole,
  setEditRolePermission,
}) => {
  const handleRoleAction = useCallback(async (data, editRole) => {
    if (rolePermissions.loading) return;
    
    try {
      const payload = editRole 
        ? { ...data, app_role_id: editRole?.app_role_id }
        : data;
      
      const endpoint = editRole 
        ? API_ENDPOINTS.EDIT_ROLE 
        : API_ENDPOINTS.CREATE_ROLE;
      
      const response = await commonPostApi(endpoint, payload);
      
      if (response?.statusText === "Created" || response?.statusText === "OK") {
        showToast(
          TOAST_TYPE.SUCCESS,
          editRole ? FEATURE_MSG.ROLE.UPDATED : FEATURE_MSG.ROLE.ADDED
        );
        setAddNewRole(false);
        setEditRole(null);
        
        if (campaignValue) {
          fetchRolesByCamp(campaignValue);
          // Refresh permissions table when role is edited (to show updated role name in permissions table)
          if (editRole && fetchPermissionsTable) {
            const minimalNavigator = { page: 1, rows: 10, searchCriteria: {} };
            fetchPermissionsTable(minimalNavigator, null);
          }
        }
      } else {
        showToast(TOAST_TYPE.ERROR, COMMON_MSG.ERROR.GENERIC);
      }
    } catch (error) {
      console.error("Error saving role:", error);
      showToast(TOAST_TYPE.ERROR, FEATURE_MSG.ROLE.ALREADY_EXISTS);
    }
  }, [rolePermissions.loading, campaignValue, fetchRolesByCamp, fetchPermissionsTable, setAddNewRole, setEditRole]);

  const handleAddPermissions = useCallback(async (editRolePermission) => {
    if (!editRolePermission || rolePermissions.assignedPermissions.length === 0) return;
    
    const permissionIds = rolePermissions.assignedPermissions.map(
      p => p?.app_permission_id
    );
    //here calling api to add permissions to role
    const success = await rolePermissions.addPermissionsToRole(
      editRolePermission?.app_role_id,
      permissionIds
    );
    
    if (success) {
      setEditRolePermission(null);
      rolePermissions.setAssignedPermissions([]);
      rolePermissions.setAvailablePermissions(rolePermissions.permissionsList);
      // Refresh both the full list (for modal) and the table view
      const minimalNavigator = { page: 1, rows: 10, searchCriteria: {} };
      rolePermissions.fetchAllPermissionsList();
      fetchPermissionsTable(minimalNavigator, null);
    }
  }, [rolePermissions, fetchPermissionsTable, setEditRolePermission]);

  return {
    handleRoleAction,
    handleAddPermissions,
  };
};

export default useRoleActions;
